import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  CircularProgress,
  IconButton,
  Badge,
  Tooltip,
  Menu,
  MenuItem,
  Popover,
  Grid,
  InputAdornment,
  Snackbar,
  Alert,
  AlertTitle,
} from '@mui/material';
import { 
  Send as SendIcon,
  Image as ImageIcon,
  EmojiEmotions as EmojiIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon,
  AttachFile as AttachFileIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../utils/dateLocale';
import api, { fileApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { API_URL } from '../config';

// Декларираме типовете, за да избегнем грешки
declare global {
  interface Window {
    EmojiMart: {
      Picker: any;
      data: any;
    }
  }
}

// Мокнат список със съобщения
const MOCK_MESSAGES: {[key: number]: Message[]} = {
  1: [
    {
      id: 101,
      sender_id: 1,
      recipient_id: 0, // Ще бъде заменено с текущия потребител
      task_id: null,
      content: 'Здравейте, искам да попитам за оферта за ремонт на баня.',
      created_at: new Date(Date.now() - 3600000 * 24).toISOString(),
      is_read: true,
      status: 'read',
      type: 'text'
    },
    {
      id: 102,
      sender_id: 0, // Ще бъде заменено с текущия потребител 
      recipient_id: 1,
      task_id: null,
      content: 'Здравейте! Разбира се, с удоволствие ще направя оферта. Какви са размерите на банята?',
      created_at: new Date(Date.now() - 3600000 * 23).toISOString(),
      is_read: true,
      status: 'read',
      type: 'text'
    },
    {
      id: 103,
      sender_id: 1,
      recipient_id: 0, // Ще бъде заменено с текущия потребител
      task_id: null,
      content: 'Банята е 6 квадратни метра. Искам цялостен ремонт с подмяна на плочки, ВиК и санитария.',
      created_at: new Date(Date.now() - 3600000 * 22).toISOString(),
      is_read: true,
      status: 'read',
      type: 'text'
    }
  ],
  2: [
    {
      id: 201,
      sender_id: 0, // Ще бъде заменено с текущия потребител
      recipient_id: 2,
      task_id: null,
      content: 'Здравейте, ремонтът ще бъде завършен до края на седмицата, както се разбрахме.',
      created_at: new Date(Date.now() - 3600000 * 6).toISOString(),
      is_read: true,
      status: 'read',
      type: 'text'
    },
    {
      id: 202,
      sender_id: 2,
      recipient_id: 0, // Ще бъде заменено с текущия потребител
      task_id: null,
      content: 'Благодаря за бързия отговор!',
      created_at: new Date(Date.now() - 3600000 * 5).toISOString(),
      is_read: true,
      status: 'read',
      type: 'text'
    }
  ],
  3: [
    {
      id: 301,
      sender_id: 3,
      recipient_id: 0, // Ще бъде заменено с текущия потребител
      task_id: null,
      content: 'Кога ще можете да дойдете да видите обекта?',
      created_at: new Date(Date.now() - 86400000 * 2).toISOString(),
      is_read: true,
      status: 'read',
      type: 'text'
    },
    {
      id: 302,
      sender_id: 0, // Ще бъде заменено с текущия потребител
      recipient_id: 3,
      task_id: null,
      content: 'Мога да дойда утре следобед, около 14:00 часа. Удобно ли е?',
      created_at: new Date(Date.now() - 86400000 * 1.5).toISOString(),
      is_read: true,
      status: 'read',
      type: 'text'
    },
    {
      id: 303,
      sender_id: 3,
      recipient_id: 0, // Ще бъде заменено с текущия потребител
      task_id: null,
      content: 'Да, идеално е. Ще ви очаквам. Адресът е ул. Цар Борис III №45.',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      is_read: false,
      status: 'delivered',
      type: 'text'
    }
  ]
};

interface User {
  id: string;
  name: string;
  avatar_url?: string;
}

interface MessageFile {
  id: number;
  filename: string;
  file_type: string;
  file_size: number;
  url: string;
  thumbnail_url?: string;
}

interface Message {
  id: number;
  sender_id: number;
  recipient_id: number;
  task_id: number | null;
  content: string;
  created_at: string;
  is_read: boolean;
  status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  type: 'text' | 'image';
  image_url?: string;
  files?: MessageFile[];
}

interface ChatInterfaceProps {
  otherUserId: number;
  otherUserName: string;
  otherUserAvatarUrl?: string;
  taskId?: number;
}

const ConnectionStatus: React.FC<{
  isConnected: boolean;
  lastError: string | null;
  reconnect: () => void;
}> = ({ isConnected, lastError, reconnect }) => {
  const { t } = useTranslation();
  
  if (isConnected) return null;
  
  return (
    <Alert 
      severity="error" 
      sx={{ mb: 2 }}
      data-testid="chat-connection-alert"
      action={
        <Button color="inherit" size="small" onClick={reconnect} data-testid="chat-reconnect-button">
          {t('chat.refresh')}
        </Button>
      }
    >
      <AlertTitle>{t('chat.disconnected')}</AlertTitle>
      {lastError || t('chat.websocket_disconnected')}
    </Alert>
  );
};

const resolveAvatarUrl = (avatarUrl?: string): string | undefined => {
  if (!avatarUrl) {
    return undefined;
  }

  if (/^(https?:\/\/|data:|blob:)/i.test(avatarUrl)) {
    return avatarUrl;
  }

  const normalizedPath = avatarUrl.startsWith('/') ? avatarUrl : `/${avatarUrl}`;
  return `${API_URL}${normalizedPath}`;
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({ otherUserId, otherUserName, otherUserAvatarUrl, taskId }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { socket, sendMessage: sendWebSocketMessage, isConnected, lastError, reconnect } = useWebSocket();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const recentlySentMessageIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Зареждаме реални съобщения от API
    fetchMessages();
  }, [otherUserId, user?.id]);

  useEffect(() => {
    // Използваме event listener вместо direct onmessage assignment,
    // за да не презаписваме listener-и на други компоненти.
    if (!socket) return;

    const handleSocketMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'chat_message' || data.type === 'message' || data.type === 'new_message') {
          const newMessage = data.payload || data.message || data;

          // Проверяваме дали съобщението е за този разговор
          const isForThisConversation = newMessage &&
            ((newMessage.sender_id === otherUserId && newMessage.recipient_id === user?.id) ||
              (newMessage.sender_id === user?.id && newMessage.recipient_id === otherUserId));

          if (isForThisConversation) {
            // Добавяме съобщението само ако го няма вече (проверяваме по ID и съдържание)
            setMessages(prev => {
              // Проверяваме дали това съобщение е току що изпратено от нас
              if (recentlySentMessageIds.current.has(newMessage.id)) {
                return prev;
              }

              // Проверяваме дали съобщението вече съществува по ID
              const existsById = prev.some(msg => msg.id === newMessage.id);

              // Проверяваме дали има съобщение със същото съдържание и време (в рамките на 10 секунди)
              // Важно: трябва да работи и за празни съобщения (само с файлове)
              const existsByContent = prev.some(msg => {
                // Ако sender_id не съвпада, не е същото съобщение
                if (msg.sender_id !== newMessage.sender_id) return false;

                // Проверяваме дали времената са близки (до 10 секунди разлика)
                const msgTime = new Date(msg.created_at).getTime();
                const newMsgTime = new Date(newMessage.created_at).getTime();
                if (Math.abs(msgTime - newMsgTime) > 10000) return false;

                // Проверяваме дали съдържанието съвпада (включително празни стрингове)
                if (msg.content !== newMessage.content) return false;

                // Ако има файлове, проверяваме дали броят съвпада
                if (msg.files?.length !== newMessage.files?.length) return false;

                return true;
              });

              if (existsById) {
                return prev; // Просто игнорираме дублиращото се съобщение
              }

              if (existsByContent) {
                // Ако съобщението съществува по съдържание, обновяваме го с пълната информация от сървъра
                const updated = prev.map(msg => {
                  const isSameMessage = msg.sender_id === newMessage.sender_id &&
                    msg.content === newMessage.content &&
                    Math.abs(new Date(msg.created_at).getTime() - new Date(newMessage.created_at).getTime()) < 10000 &&
                    msg.files?.length === newMessage.files?.length;

                  if (isSameMessage) {
                    return { ...newMessage, status: 'sent' as const };
                  }
                  return msg;
                });

                // Премахваме дублиращи се съобщения (ако има повече от едно с реалното ID)
                return updated.filter((msg, index, self) =>
                  index === self.findIndex(m => m.id === msg.id)
                );
              }

              return [...prev, { ...newMessage, status: 'sent' as const }];
            });
            scrollToBottom();
          }
        }
      } catch (error) {
        console.error('ChatInterface: Error parsing WebSocket message:', error);
      }
    };

    socket.addEventListener('message', handleSocketMessage);
    return () => socket.removeEventListener('message', handleSocketMessage);
  }, [socket, otherUserId, user?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async (loadMore = false) => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const skip = loadMore ? messages.length : 0;
      const baseUrl = `/api/v1/chat-messages/with/${otherUserId}?skip=${skip}&limit=20`;
      const url = taskId ? `${baseUrl}&task_id=${taskId}` : baseUrl;
      const response = await api.get(url);
      
      const data = response.data;
      
      if (data.length < 20) {
        setHasMore(false);
      }
      
      const sortedMessages = data.sort((a: Message, b: Message) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      
      if (loadMore) {
        setMessages(prev => [...sortedMessages, ...prev]);
      } else {
        setMessages(sortedMessages);
      }
      
    } catch (err) {
      console.error('ChatInterface: Error fetching messages:', err);
      setError(t('chat.errors.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async (messageIds: number[]) => {
    // Използваме локална логика вместо API заявка
    setMessages(prev => 
      prev.map(msg => 
        messageIds.includes(msg.id) 
          ? { ...msg, is_read: true, status: 'read' as const } 
          : msg
      )
    );
  };

  const markMessageAsRead = async (messageId: number) => {
    await markMessagesAsRead([messageId]);
  };

  const handleDeleteFile = async (fileId: number, messageId: number) => {
    if (!window.confirm(t('chat.confirm_delete_file', 'Сигурни ли сте, че искате да изтриете този файл?'))) {
      return;
    }

    try {
      await fileApi.deleteFile(fileId);
      
      // Обновяваме локално съобщението да премахне файла
      setMessages(prev => 
        prev.map(msg => {
          if (msg.id === messageId && msg.files) {
            return {
              ...msg,
              files: msg.files.filter(f => f.id !== fileId)
            };
          }
          return msg;
        })
      );
    } catch (error) {
      console.error('Error deleting file:', error);
      showNotification(t('chat.error_deleting_file', 'Грешка при изтриване на файл'), 'error');
    }
  };

  const sendMessage = async () => {
    // Проверяваме дали има съобщение за изпращане
    if ((!newMessage.trim() && !selectedImage) || !user?.id) {
      return;
    }
    
    // Създаваме ново съобщение локално
    const messageContent = newMessage.trim();
    const imageToUpload = selectedImage;
    setNewMessage('');
    setSelectedImage(null);
    
    // Генерираме уникално ID
    const tempId = Date.now();
    
    // Създаваме обект на съобщението
    const message: Message = {
      id: tempId,
      sender_id: user.id,
      recipient_id: otherUserId,
      task_id: taskId || null,
      content: messageContent || (imageToUpload ? '📎' : ''),
      created_at: new Date().toISOString(),
      is_read: false,
      status: 'sending',
      type: 'text'
    };
    
    // Добавяме го към списъка със съобщения
    setMessages(prev => [...prev, message]);
    scrollToBottom();
    
    try {
      // Първо качваме файла ако има
      // ВАЖНО: НЕ подаваме task_id - файловете от чата са само за съобщения
      let uploadedFileIds: number[] = [];
      if (imageToUpload) {
        const formData = new FormData();
        formData.append('file', imageToUpload);
        // НЕ добавяме task_id - файловете са само за чата, не за задачата
        
        const uploadResponse = await api.post('/api/v1/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const uploadedFile = uploadResponse.data;
        uploadedFileIds.push(uploadedFile.id);
      }
      
      // Сега изпращаме съобщението с ID-тата на файловете
      const response = await api.post('/api/v1/chat-messages', {
        recipient_id: otherUserId,
        content: messageContent || (imageToUpload ? '' : ''),
        task_id: taskId || null,
        file_ids: uploadedFileIds.length > 0 ? uploadedFileIds : undefined
      });
      
      const sentMessage = response.data;
      
      // Добавяме реалното ID в списъка със скорошно изпратени съобщения
      recentlySentMessageIds.current.add(sentMessage.id);
      
      // След 15 секунди премахваме ID-то от списъка
      setTimeout(() => {
        recentlySentMessageIds.current.delete(sentMessage.id);
      }, 15000);
      
      // Обновяваме съобщението с реалното ID, статус и пълна информация
      setMessages(prev => {
        const updated = prev.map(msg => 
          msg.id === tempId 
            ? { 
                ...sentMessage,
                status: 'sent' as const 
              } 
            : msg
        );
        
        // Премахваме дублиращи се съобщения (ако има повече от едно с реалното ID)
        const deduplicated = updated.filter((msg, index, self) => 
          index === self.findIndex(m => m.id === msg.id)
        );

        return deduplicated;
      });
      
    } catch (err) {
      console.error('ChatInterface: Error sending message:', err);
      // Маркираме съобщението като неуспешно
      setMessages(prev => 
        prev.map(msg => 
          msg.id === tempId 
            ? { ...msg, status: 'failed' as const } 
            : msg
        )
      );
      setSnackbarMessage(t('chat.errors.send_failed'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getLocale = () => getDateFnsLocale(i18n.language);

  const formatDate = (date: string) => {
    try {
      return format(new Date(date), 'HH:mm', { locale: getLocale() });
    } catch (error) {
      console.error('Error formatting date:', error);
      return date;
    }
  };

  const isCurrentUser = (senderId: number) => {
    return user?.id === senderId;
  };

  const sendTypingStatus = (isTyping: boolean) => {
    if (socket && isConnected) {
      sendWebSocketMessage({
        type: 'typing',
        receiver_id: otherUserId,
        is_typing: isTyping
      });
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    
    // Отменяме предишния таймаут, ако има такъв
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    // Изпращаме статус, че пишем
    sendTypingStatus(true);
    
    // Задаваме таймаут, след който ще изпратим статус, че не пишем вече
    const timeout = setTimeout(() => {
      sendTypingStatus(false);
    }, 2000);
    
    setTypingTimeout(timeout);
  };

  const loadMoreMessages = () => {
    const newPage = page + 1;
    setPage(newPage);
    fetchMessages(true);
  };

  const handleScroll = () => {
    if (!messagesContainerRef.current || loading || !hasMore) return;
    
    const { scrollTop } = messagesContainerRef.current;
    
    if (scrollTop === 0) {
      loadMoreMessages();
    }
  };

  const handleEmojiClick = (emoji: any) => {
    setNewMessage(prev => prev + emoji.native);
    setEmojiAnchorEl(null);
  };

  const handleEmojiOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleOpenFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    if (!file.type.startsWith('image/')) {
      showNotification(t('chat.errors.upload_failed'), 'error');
      return;
    }
    
    // Максимален размер на файла: 5MB
    if (file.size > 5 * 1024 * 1024) {
      showNotification(t('chat.errors.upload_failed'), 'error');
      return;
    }
    
    setSelectedImage(file);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const resendMessage = async (message: Message) => {
    // Обновяваме статуса на съобщението
    setMessages(prev => 
      prev.map(msg => 
        msg.id === message.id 
          ? { ...msg, status: 'sending' } 
          : msg
      )
    );
    
    // Имитираме изпращане със забавяне
    setTimeout(() => {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === message.id 
            ? { ...msg, status: 'sent' } 
            : msg
        )
      );
      
      // След още малко закъснение, симулираме доставяне
      setTimeout(() => {
        setMessages(prev => 
          prev.map(msg => 
            msg.id === message.id 
              ? { ...msg, status: 'delivered' } 
              : msg
          )
        );
      }, 1000);
    }, 1500);
  };

  const renderMessageStatus = (message: Message) => {
    if (message.status === 'sending') {
      return <CircularProgress size={10} sx={{ ml: 1 }} />;
    } else if (message.status === 'sent') {
      return <CheckIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />;
    } else if (message.status === 'delivered') {
      return <DoneAllIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />;
    } else if (message.status === 'read') {
      return <DoneAllIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />;
    } else if (message.status === 'failed') {
      return (
        <Tooltip title={t('chat.send_failed')}>
          <IconButton size="small" onClick={() => resendMessage(message)}>
            <ErrorIcon fontSize="small" color="error" />
          </IconButton>
        </Tooltip>
      );
    }
    return null;
  };

  const canSendMessage = isConnected && !loading;
  const currentUserAvatarSrc = resolveAvatarUrl(user?.avatar_url);
  const otherUserAvatarSrc = resolveAvatarUrl(otherUserAvatarUrl);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Заглавие на чата */}
      <Box 
        sx={{ 
          p: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          bgcolor: 'background.paper'
        }}
      >
        <Avatar 
          alt={otherUserName}
          src={otherUserAvatarSrc}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.src = '/default-avatar.png';
          }}
          sx={{ mr: 2 }}
        />
        <Box>
          <Typography variant="subtitle1">{otherUserName}</Typography>
          {isTyping && (
            <Typography variant="caption" color="text.secondary">
              {t('chat.typing')}...
            </Typography>
          )}
        </Box>
      </Box>

      {/* Статус на връзката */}
      <ConnectionStatus isConnected={isConnected} lastError={lastError} reconnect={reconnect} />
      
      {/* Контейнер със съобщения */}
      <Box 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'grey.50',
        }}
      >
        {/* Индикатор за зареждане */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={30} />
          </Box>
        )}
        
        {/* Бутон за зареждане на още съобщения */}
        {hasMore && !loading && messages.length > 0 && (
          <Button 
            variant="text" 
            onClick={loadMoreMessages} 
            sx={{ alignSelf: 'center', mb: 2 }}
          >
            {t('chat.load_more')}
          </Button>
        )}
        
        {/* Показваме грешка, ако има такава */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => fetchMessages()}>
                {t('common.retry', 'Опитай отново')}
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        
        {/* Списък със съобщения */}
        {messages.length === 0 && !loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
            <Typography color="text.secondary">
              {t('chat.no_messages')}
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => {
            const isUser = isCurrentUser(message.sender_id);
            const showAvatar = !isUser && (index === 0 || messages[index - 1].sender_id !== message.sender_id);
            
            const avatarSrc = message.sender_id === user?.id
              ? currentUserAvatarSrc
              : otherUserAvatarSrc;

            return (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                {!isUser && showAvatar && (
                  <Avatar 
                    alt={otherUserName}
                    src={avatarSrc}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.src = '/default-avatar.png';
                    }}
                    sx={{ mr: 1, width: 36, height: 36, alignSelf: 'flex-end' }}
                  />
                )}
                
                {!isUser && !showAvatar && <Box sx={{ width: 36, mr: 1 }} />}
                
                <Box
                  sx={{
                    maxWidth: '70%',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: isUser ? 'primary.light' : 'background.paper',
                    color: isUser ? 'primary.contrastText' : 'text.primary',
                    boxShadow: 1,
                    position: 'relative',
                    '&::after': isUser ? {
                      content: '""',
                      position: 'absolute',
                      right: '-10px',
                      bottom: '20px',
                      width: 0,
                      height: 0,
                      border: '10px solid transparent',
                      borderLeftColor: 'primary.light',
                      borderRight: 0,
                      marginRight: '-10px',
                    } : {},
                    '&::before': !isUser ? {
                      content: '""',
                      position: 'absolute',
                      left: '-10px',
                      bottom: '20px',
                      width: 0,
                      height: 0,
                      border: '10px solid transparent',
                      borderRightColor: 'background.paper',
                      borderLeft: 0,
                      marginLeft: '-10px',
                    } : {},
                  }}
                >
                  {message.type === 'image' ? (
                    <Box 
                      component="img" 
                      src={message.image_url} 
                      alt={t('chat.image')}
                      sx={{ 
                        maxWidth: '100%', 
                        borderRadius: 1,
                        cursor: 'pointer',
                      }}
                    />
                  ) : (
                    <>
                      {message.content && (
                        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                          {message.content}
                        </Typography>
                      )}
                      {message.files && message.files.length > 0 && (
                        <Box sx={{ mt: message.content ? 1 : 0 }}>
                          {message.files.map((file) => (
                            <Box
                              key={file.id}
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                p: 1,
                                mb: 0.5,
                                bgcolor: isUser ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.03)',
                                borderRadius: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: isUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                                  '& .delete-btn': {
                                    opacity: 1,
                                  }
                                }
                              }}
                              onClick={() => window.open(file.url, '_blank')}
                            >
                              {file.file_type.startsWith('image/') && file.thumbnail_url ? (
                                <Box
                                  component="img"
                                  src={file.thumbnail_url}
                                  alt={file.filename}
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    mr: 1
                                  }}
                                />
                              ) : (
                                <AttachFileIcon sx={{ mr: 1, color: isUser ? 'primary.contrastText' : 'text.secondary' }} />
                              )}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="body2" 
                                  noWrap 
                                  sx={{ 
                                    color: isUser ? 'primary.contrastText' : 'text.primary',
                                    fontWeight: 500
                                  }}
                                >
                                  {file.filename}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: isUser ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                                  }}
                                >
                                  {(file.file_size / 1024).toFixed(1)} KB
                                </Typography>
                              </Box>
                              
                              {/* Delete Button - само за собственика на съобщението */}
                              {isUser && (
                                <IconButton
                                  className="delete-btn"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFile(file.id, message.id);
                                  }}
                                  sx={{
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    color: isUser ? 'error.light' : 'error.main',
                                    '&:hover': {
                                      bgcolor: 'error.main',
                                      color: 'white',
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mt: 1
                  }}>
                    <Typography variant="caption" color={isUser ? 'primary.contrastText' : 'text.secondary'} sx={{ opacity: 0.7 }}>
                      {formatDate(message.created_at)}
                    </Typography>
                    {isUser && renderMessageStatus(message)}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
        
        {/* Референция за скролиране до края */}
        <div ref={messagesEndRef} />
      </Box>
      
      {/* Прикачен файл */}
      {selectedImage && (
        <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={URL.createObjectURL(selectedImage)}
              alt="Selected"
              sx={{ 
                width: 60, 
                height: 60, 
                objectFit: 'cover',
                borderRadius: 1,
                mr: 1
              }}
            />
            <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>
              {selectedImage.name}
            </Typography>
            <IconButton size="small" onClick={handleRemoveImage}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
      
      {/* Форма за писане */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid', 
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleImageSelect}
            />
            <IconButton onClick={handleOpenFileDialog} disabled={!canSendMessage}>
              <AttachFileIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton onClick={handleEmojiOpen} disabled={!canSendMessage}>
              <EmojiIcon />
            </IconButton>
            <Popover
              open={Boolean(emojiAnchorEl)}
              anchorEl={emojiAnchorEl}
              onClose={handleEmojiClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
            >
              <Box sx={{ width: 320, height: 350 }}>
                <Picker 
                  data={data} 
                  onEmojiSelect={handleEmojiClick}
                  locale={i18n.language}
                />
              </Box>
            </Popover>
          </Grid>
          <Grid item xs>
            <TextField
              fullWidth
              placeholder={t('chat.type_message')}
              variant="outlined"
              value={newMessage}
              onChange={handleMessageChange}
              onKeyPress={handleKeyPress}
              disabled={!canSendMessage}
              multiline
              maxRows={3}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={sendMessage} 
                      disabled={(!newMessage.trim() && !selectedImage) || !canSendMessage}
                      color="primary"
                      data-testid="chat-send-button"
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{ 'data-testid': 'chat-message-input' }}
            />
          </Grid>
        </Grid>
      </Box>
      
      {/* Известие */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatInterface; 